import { REMINDERS_PAUSED_REASONS, resolveRemindersPausedReason } from '@shared/core/billing'
import type { GetProfileResponseDto } from '@shared/core/users'
import {
  SUBSCRIPTION_POLL_INTERVAL_MS,
  SUBSCRIPTION_POLL_TIMEOUT_MS,
} from 'src/modules/payment/constants/plans-offer.constants'

export type RefetchProfile = () => Promise<{ data?: GetProfileResponseDto }>

type ProfileCheck = (profile: GetProfileResponseDto) => boolean

// Paddle's subscription webhook lands after the checkout closes; `false` means the bound expired.
export function waitForSubscription(
  refetchProfile: RefetchProfile,
  previousSubscriptionId: number | null,
): Promise<boolean> {
  return pollProfileUntil(refetchProfile, (profile) => hasNewSubscription(profile, previousSubscriptionId))
}

export function waitForPaymentResumed(refetchProfile: RefetchProfile): Promise<boolean> {
  return pollProfileUntil(
    refetchProfile,
    (profile) => resolveRemindersPausedReason(profile) !== REMINDERS_PAUSED_REASONS.paymentPaused,
  )
}

// An ended account keeps its old subscription id until the new subscription's webhook replaces it.
function hasNewSubscription(profile: GetProfileResponseDto, previousSubscriptionId: number | null): boolean {
  if (!profile.billing_subscription_id) return false
  if (profile.billing_subscription_id === previousSubscriptionId) return false

  return resolveRemindersPausedReason(profile) !== REMINDERS_PAUSED_REASONS.subscriptionEnded
}

async function pollProfileUntil(refetchProfile: RefetchProfile, isSettled: ProfileCheck): Promise<boolean> {
  for (let elapsed = 0; elapsed < SUBSCRIPTION_POLL_TIMEOUT_MS; elapsed += SUBSCRIPTION_POLL_INTERVAL_MS) {
    await delay(SUBSCRIPTION_POLL_INTERVAL_MS)

    if (await isProfileSettled(refetchProfile, isSettled)) {
      return true
    }
  }

  return false
}

async function isProfileSettled(refetchProfile: RefetchProfile, isSettled: ProfileCheck): Promise<boolean> {
  try {
    const { data } = await refetchProfile()

    return Boolean(data) && isSettled(data)
  } catch {
    return false
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
